import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/base/DramaCard";
import PlayerModal from "@/components/feature/PlayerModal";
import EmptyState from "@/components/base/EmptyState";
import DetailHero from "@/pages/detail/components/DetailHero";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { formatDate, getDramaById, getRecommendations } from "@/lib/drama";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id } = useParams();
  const drama = getDramaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playerEpisode, setPlayerEpisode] = useState<number | null>(null);

  useDocumentMeta({
    title: drama
      ? `${drama.title} - 高清电视剧 - 嫂子的职业在线观看`
      : "未找到该电视剧 - 嫂子的职业在线观看",
    description: drama
      ? `《${drama.title}》${drama.year} 年${drama.region}${drama.genres.join("、")}剧，${drama.status}。主演 ${drama.cast.join("、")}，导演 ${drama.director}。${drama.synopsis.slice(0, 60)}`
      : "该剧集可能已下架或链接有误，返回首页继续浏览其他高清电视剧。",
    keywords: drama
      ? `${drama.title},${drama.title}在线观看,${drama.genres.join(",")},高清电视剧`
      : "电视剧在线观看,高清电视剧",
  });

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-100">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[1280px] px-4 pt-28 md:px-8 md:pt-36">
          <EmptyState
            icon="ri-file-unknow-line"
            title="未找到该电视剧"
            description="这部剧可能已下架，或者链接地址有误。你可以返回首页查看最新更新的剧集。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line" aria-hidden="true" />
                返回首页
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const recommendations = getRecommendations(drama, 4);
  const favorite = isFavorite(drama.id);

  const infoRows = [
    { label: "导演", value: drama.director },
    { label: "主演", value: drama.cast.join("、") },
    { label: "类型", value: drama.genres.join(" / ") },
    { label: "地区", value: drama.region },
    { label: "年份", value: String(drama.year) },
    { label: "集数", value: `共 ${drama.totalEpisodes} 集` },
    { label: "状态", value: drama.status },
    { label: "最近更新", value: formatDate(drama.updatedAt) },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />

      <main>
        <DetailHero
          drama={drama}
          isFavorite={favorite}
          onToggleFavorite={() => toggleFavorite(drama.id)}
          onPlay={() => setPlayerEpisode(Math.min(drama.updatedEpisodes, drama.totalEpisodes))}
        />

        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6">
            <div className="min-w-0 space-y-5">
              <section
                aria-labelledby="synopsis-title"
                className="rounded-lg border border-background-200 bg-background-50"
              >
                <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3 md:px-5">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-100 text-primary-700">
                    <i className="ri-book-open-line text-sm" aria-hidden="true" />
                  </span>
                  <h2 id="synopsis-title" className="font-heading text-base font-bold text-foreground-950 md:text-lg">
                    剧情简介
                  </h2>
                </div>
                <div className="px-4 py-4 md:px-5 md:py-5">
                  <p className="text-sm leading-7 text-foreground-700">{drama.synopsis}</p>
                  <p className="mt-3 text-sm leading-7 text-foreground-700">
                    本剧以细腻的日常叙事展开人物关系，围绕工作、家庭与自我选择三条线索推进，
                    适合喜欢现实题材与人物刻画的观众。剧集资料与演职信息均可在本页查看，
                    如需了解更多同题材作品，可继续浏览下方的相关推荐。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {drama.genres.map((genre) => (
                      <Link
                        key={genre}
                        to={`/category?genre=${encodeURIComponent(genre)}`}
                        className="rounded-full bg-primary-50 px-2.5 py-1 text-[11px] text-primary-700 transition-colors hover:bg-primary-100"
                      >
                        {genre}
                      </Link>
                    ))}
                  </div>
                </div>
              </section>

              <EpisodeList drama={drama} onSelect={(episode) => setPlayerEpisode(episode)} />

              <section aria-labelledby="recommend-title">
                <div className="mb-4 flex items-end justify-between gap-3">
                  <div>
                    <h2 id="recommend-title" className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                      相关推荐
                    </h2>
                    <p className="mt-1 text-xs text-foreground-500">
                      根据「{drama.genres.join(" · ")}」题材为你自动关联
                    </p>
                  </div>
                  <Link
                    to={`/category?genre=${encodeURIComponent(drama.genres[0])}`}
                    className="shrink-0 whitespace-nowrap text-xs text-primary-600 transition-colors hover:text-primary-800 md:text-sm"
                  >
                    更多同类
                    <i className="ri-arrow-right-s-line ml-0.5" aria-hidden="true" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
                  {recommendations.map((item) => (
                    <DramaCard key={item.id} drama={item} />
                  ))}
                </div>
              </section>
            </div>

            <aside className="space-y-4">
              <section className="rounded-lg border border-background-200 bg-background-50">
                <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-secondary-100 text-secondary-700">
                    <i className="ri-file-list-3-line text-sm" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-sm font-bold text-foreground-950">剧集信息</h2>
                </div>
                <dl className="px-4 py-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 border-b border-background-100 py-2 last:border-0">
                      <dt className="w-16 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 text-xs leading-relaxed text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="rounded-lg border border-background-200 bg-background-50 p-4">
                <button
                  type="button"
                  onClick={() => toggleFavorite(drama.id)}
                  aria-pressed={favorite}
                  className={`flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    favorite
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} aria-hidden="true" />
                  {favorite ? "已收藏 · 点击取消" : "收藏这部剧"}
                </button>
                <p className="mt-2.5 text-[11px] leading-relaxed text-foreground-500">
                  {favorite
                    ? "已保存到本机浏览器的收藏夹，清除浏览器数据后会丢失。"
                    : "收藏记录保存在本机浏览器中，刷新页面不会丢失。"}
                </p>
              </section>

              <section className="rounded-lg bg-primary-950 p-4">
                <p className="flex items-center gap-1.5 text-xs font-medium text-background-100">
                  <i className="ri-shield-check-line text-accent-400" aria-hidden="true" />
                  观看提示
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-background-400">
                  本站为演示站点，播放功能仅展示前端交互流程，不提供任何真实视频流或资源下载。
                </p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-accent-400 transition-colors hover:text-accent-300"
                >
                  了解网站定位
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </section>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />

      <PlayerModal
        open={playerEpisode !== null}
        title={drama.title}
        episodeLabel={playerEpisode ? `第 ${playerEpisode} 集` : ""}
        onClose={() => setPlayerEpisode(null)}
      />
    </div>
  );
}